import { Habit, HabitSchedule, Timestamp } from '@griply/types';
import { useMemo } from 'react';

import { useTimeframeCompletions } from './use-timeframe-completions';
import { useTimeframeEntries } from './use-timeframe-entries';
import { useTimeframeOccurrences } from './use-timeframe-occurrences';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export const useHabitProgress = (
  habit: Habit,
  { startDate, endDate }: Options,
) => {
  const { occurrences, timeframeOccurrences } = useTimeframeOccurrences(
    habit.schedules,
    { startDate, endDate },
  );
  const { completions, timeframeCompletions } = useTimeframeCompletions(
    habit.schedules,
    { startDate, endDate },
  );
  const { dateEntries, timeframeDateEntries } = useTimeframeEntries(
    habit.schedules,
    {
      startDate,
      endDate,
    },
  );

  const scheduleChanges = useMemo(
    () =>
      [...habit.schedules]
        .sort(
          (scheduleA, scheduleB) =>
            scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
        )
        .map((schedule, index, arr) => {
          if (!schedule.endDate) {
            return undefined;
          }

          const newSchedule = arr[index + 1];
          if (!newSchedule) {
            return undefined;
          }

          return {
            date: schedule.endDate,
            oldSchedule: schedule,
            newSchedule: newSchedule,
          };
        })
        .filter(Boolean) as {
        date: Timestamp;
        oldSchedule: HabitSchedule;
        newSchedule: HabitSchedule;
      }[],
    [habit.schedules],
  );

  return {
    dateEntries,
    timeframeDateEntries,
    occurrences,
    timeframeOccurrences,
    completions,
    timeframeCompletions,
    scheduleChanges,
  };
};
